import { useEffect, useState, type FormEvent } from "react";
import {
  Eye,
  EyeOff,
  KeyRound,
  LogIn,
  ShieldCheck,
} from "lucide-react";
import { api, ApiError } from "../api";
import type { AppSettings } from "../types";
import { Alert, Button, Field } from "../components/ui";

export function LoginScreen({
  settings,
  onLogin,
}: {
  settings: AppSettings;
  onLogin: () => Promise<void> | void;
}) {
  const [loginCode, setLoginCode] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api<{ mustChangePassword: boolean }>("/api/auth/login", {
        method: "POST",
        body: { loginCode, password, remember },
      });
      await onLogin();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Giriş yapılamadı.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-hero" aria-label="Uygulama tanıtımı">
        <div className="auth-brand">
          <img src="/brand/logo-icon.svg" alt="" width="68" height="68" />
          <div>
            <span>İzin ve vardiya takibi</span>
          </div>
        </div>
        <div className="auth-hero__orbit" aria-hidden="true">
          <span>A</span><span>B</span><span>C</span><span>D</span>
        </div>
      </section>
      <section className="auth-form-shell">
        <section className="auth-card" aria-labelledby="login-title">
          <div className="auth-card__heading">
            <span className="eyebrow">GÜVENLİ GİRİŞ</span>
            <h1 id="login-title">{settings.appName}</h1>
            <p className="auth-lead">İzin ve vardiya bilgilerinin kolayca takip edilmesi için yapılmıştır.</p>
          </div>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <form onSubmit={submit} className="form-stack">
            <Field
              label="Sicil / giriş kodu"
              name="loginCode"
              inputMode="numeric"
              autoComplete="username"
              value={loginCode}
              onChange={(event) => setLoginCode(event.target.value)}
              required
              placeholder="Sicil numaranız"
            />
            <div className="password-field">
              <Field
                label="Parola"
                name="password"
                type={show ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                placeholder="Parolanız"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShow((value) => !value)}
                aria-label={show ? "Parolayı gizle" : "Parolayı göster"}
              >
                {show ? <EyeOff /> : <Eye />}
              </button>
            </div>
            <label className="check-row">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
              />
              <span>
                <strong>Beni hatırla</strong>
                <small>Çıkış yapana kadar oturumunuz açık kalır.</small>
              </span>
            </label>
            <Button type="submit" loading={loading}>
              <LogIn size={19} />
              Giriş yap
            </Button>
          </form>
          <div className="auth-note">
            <ShieldCheck size={19} />
            <span>Bu uygulama bilgilendirme ve takip amaçlıdır; resmî izin sistemi değildir.</span>
          </div>
        </section>
      </section>
    </main>
  );
}

export function ChangePasswordScreen({
  settings,
  onDone,
}: {
  settings: AppSettings;
  onDone: () => Promise<void> | void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password.length < 4)
      return setError("Yeni parola en az 4 karakter olmalıdır.");
    if (password !== confirm)
      return setError("Parolalar birbiriyle eşleşmiyor.");
    setLoading(true);
    setError("");
    try {
      await api("/api/auth/change-password", {
        method: "POST",
        body: { newPassword: password },
      });
      await onDone();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : "Parola değiştirilemedi.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-hero auth-hero--compact" aria-label="Uygulama tanıtımı">
        <div className="auth-brand">
          <img src="/brand/logo-icon.svg" alt="" width="68" height="68" />
          <div><strong>{settings.appName}</strong><span>Güvenli hesap kurulumu</span></div>
        </div>
        <div className="auth-hero__copy">
          <span className="auth-kicker">İLK GİRİŞ</span>
          <h1>Hesabınızı güvence altına alın.</h1>
          <p>Geçici parola yerine yalnızca sizin bildiğiniz yeni parolayı belirleyin.</p>
        </div>
      </section>
      <section className="auth-form-shell">
        <section className="auth-card" aria-labelledby="password-title">
          <div className="auth-card__heading auth-card__heading--icon">
            <span className="auth-step-icon"><KeyRound /></span>
            <div><span className="eyebrow">PAROLA GÜVENLİĞİ</span><h2 id="password-title">Yeni parolanızı belirleyin</h2></div>
          </div>
          <p className="auth-lead">En az 4 karakterden oluşan kişisel bir parola seçin.</p>
          {error ? <Alert tone="danger">{error}</Alert> : null}
          <form className="form-stack" onSubmit={submit}>
            <div className="password-field">
              <Field
                label="Yeni parola"
                name="newPassword"
                type={show ? "text" : "password"}
                minLength={4}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
              <button className="password-toggle" type="button" onClick={() => setShow(!show)} aria-label="Parola görünürlüğünü değiştir">
                {show ? <EyeOff /> : <Eye />}
              </button>
            </div>
            <Field
              label="Yeni parola (tekrar)"
              name="confirmPassword"
              type={show ? "text" : "password"}
              minLength={4}
              autoComplete="new-password"
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
              required
            />
            <Button type="submit" loading={loading}>Parolayı kaydet</Button>
          </form>
        </section>
      </section>
    </main>
  );
}

export function OnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online ? null : (
    <div className="offline-banner" role="status">
      Çevrimdışısınız. Kayıt değişiklikleri bağlantı geldiğinde yapılabilir.
    </div>
  );
}
